// src/pages/OrdersPage.js
import React, { useState, useEffect, useCallback } from 'react';
import styles from './OrdersPage.module.css';
import apiClient from '../services/ApiClient';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Modal from '../components/common/Modal';
import ManualEsimModal from '../components/orders/ManualEsimModal';
import { toast } from 'react-toastify';

// Durumları daha okunaklı hale getirmek için bir eşleştirme objesi
const statusDisplayMap = {
    FAILED_PRODUCT_MAPPING: 'Ürün Eşleştirme Hatası',
    FAILED_PROVIDER_API_ERROR: 'Sağlayıcı API Hatası',
    DB_SAVE_FAILED_CRITICAL: 'Veritabanı Kayıt Hatası (Kritik)',
    PROVISIONED_SUCCESSFULLY: 'Başarıyla Sağlandı',
    ACTIVATION_PENDING: 'Aktivasyon Bekleniyor',
    PENDING_PROVISIONING: 'Sağlama Bekleniyor', // YENİ DURUM
    // Sınırsız paketlerde hat açma asenkron: ödeme alındı, sağlayıcı hattı
    // hazırlıyor. Bu bir HATA DEĞİL — cron (resume-async-provisioning) bitirir.
    PENDING_PROVIDER_ASYNC: 'Sağlayıcı Hazırlıyor (Async)',
    // Diğer olası durumlar buraya eklenebilir
};

// Hangi durumların hangi sekmede gösterileceğini tanımlayalım
const tabConfig = [
    // "Aktivasyon Bekleyenler" sekmesi artık hem ACTIVATION_PENDING hem de PENDING_PROVISIONING içerebilir
    { key: 'pending', label: 'Bekleyen Siparişler', statuses: ['ACTIVATION_PENDING', 'PENDING_PROVISIONING', 'PENDING_PROVIDER_ASYNC'] },
    { key: 'successful', label: 'Başarılı Siparişler', statuses: ['PROVISIONED_SUCCESSFULLY'] },
    { key: 'failed', label: 'Başarısız Siparişler', statuses: ['FAILED_PRODUCT_MAPPING', 'FAILED_PROVIDER_API_ERROR', 'DB_SAVE_FAILED_CRITICAL'] },
];


const OrdersPage = () => {
    // const [allOrders, setAllOrders] = useState([]); // Artık tümünü değil, sadece mevcut sayfayı tutacağız
    const [displayedOrders, setDisplayedOrders] = useState([]); // Mevcut sayfadaki siparişler

    const [activeTabKey, setActiveTabKey] = useState(tabConfig[0].key);

    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    // actionLoading: aşağıdaki handleProcessOrder (yarım kalmış "siparişi işle" özelliği)
    // için ayrılmış; ilgili buton henüz tabloya eklenmedi. Bkz. handleProcessOrder notu.
    // eslint-disable-next-line no-unused-vars
    const [actionLoading, setActionLoading] = useState(null);

    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [selectedOrderForModal, setSelectedOrderForModal] = useState(null);

    const [isManualEsimModalOpen, setIsManualEsimModalOpen] = useState(false);

    // --- YENİ: Sayfalama State'leri ---
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20); // API'den gelen perPage ile senkronize olabilir
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [copiedId, setCopiedId] = useState(null); // Hangi ID'nin kopyalandığını tutar (feedback için)
    // --- /YENİ ---
    const handleCopyOrderId = (orderId, event) => {
        navigator.clipboard.writeText(orderId).then(() => {
            setCopiedId(orderId); // Kopyalanan ID'yi state'e ata
            // event.target.focus(); // Odaklanmayı koru (opsiyonel)
            setTimeout(() => {
                setCopiedId(null); // Kısa bir süre sonra feedback'i kaldır
            }, 1500); // 1.5 saniye
        }).catch(err => {
            console.error('ID kopyalanamadı: ', err);
            toast.error('ID kopyalanamadı. Lütfen manuel olarak kopyalayın.');
        });
    };
    // --- API'den Siparişleri Çekme (Sayfalama ve Filtreleme ile) ---
    const fetchOrdersForCurrentTab = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        const currentTab = tabConfig.find(tab => tab.key === activeTabKey);
        if (!currentTab) {
            setIsLoading(false);
            return;
        }

        const params = {
            page: currentPage,
            limit: itemsPerPage,
            status: currentTab.statuses.join(','), // Durumları virgülle ayırarak gönder
            // search: searchTerm, // Eğer arama da API'ye gönderilecekse
        };

        try {
            // GERÇEK API ENDPOINT'İNİZİ KULLANIN
            const response = await apiClient.get('/api/v1/admin/orders', { params });

            if (response.data && Array.isArray(response.data.data) && response.data.meta) {
                setDisplayedOrders(response.data.data); // API'den gelen o anki sayfanın verisi
                setTotalItems(response.data.meta.totalItems);
                setTotalPages(response.data.meta.totalPages);
                setCurrentPage(response.data.meta.currentPage); // API'den dönen sayfayı ayarla
                setItemsPerPage(response.data.meta.perPage); // API'den dönen limiti ayarla
            } else {
                console.warn("API'den siparişler beklenen formatta gelmedi:", response.data);
                setDisplayedOrders([]);
                setTotalItems(0);
                setTotalPages(0);
                throw new Error("Sipariş verileri alınamadı.");
            }
        } catch (err) {
            console.error("Siparişler çekilirken hata:", err);
            setError(err.message || "Siparişler yüklenirken bir sorun oluştu.");
            setDisplayedOrders([]);
            setTotalItems(0);
            setTotalPages(0);
        } finally {
            setIsLoading(false);
        }
    }, [activeTabKey, currentPage, itemsPerPage]); // searchTerm de eklenebilir

    useEffect(() => {
        fetchOrdersForCurrentTab();
    }, [fetchOrdersForCurrentTab]); // fetchOrdersForCurrentTab değiştiğinde çalışır

    // Sekme değiştiğinde veya sayfa başına öğe sayısı değiştiğinde ilk sayfaya dön
    useEffect(() => {
        setCurrentPage(1);
    }, [activeTabKey, itemsPerPage]);


    // TODO (teslim): "Siparişi işle" özelliği yarım kalmış. Bu fonksiyon hazır ancak
    // tabloda onu tetikleyen bir buton render edilmiyor. Backend endpoint'i doğrulanıp
    // renderOrderTable içine bir aksiyon butonu eklenerek tamamlanmalı.
    // eslint-disable-next-line no-unused-vars
    const handleProcessOrder = async (order) => {
        // Sadece ACTIVATION_PENDING veya PENDING_PROVISIONING durumundakiler işlenebilir
        if (!['ACTIVATION_PENDING', 'PENDING_PROVISIONING'].includes(order.status)) {
            toast.error("Sadece 'Bekleyen' durumdaki siparişler işleme alınabilir.");
            return;
        }
        if (!window.confirm(`#${order.id.substring(0, 8)}... ID'li siparişi işleme almak istediğinizden emin misiniz?`)) return;

        setActionLoading(order.id);

        try {
            // API'ye sipariş durumunu güncelleme isteği
            // API'nızın beklediği request body'yi ve yeni durumu ayarlayın
            // Örneğin, bir sonraki durum 'PROVISIONING_STARTED' veya direkt 'PROVISIONED_SUCCESSFULLY' olabilir.
            const newApiStatus = 'PROVISIONED_SUCCESSFULLY'; // Örnek hedef durum
            const requestBody = {
                newStatus: newApiStatus, // API'nızın beklediği alan adı
                // errorMessage: null // Başarılıysa hata mesajını temizle
            };

            // GERÇEK API ENDPOINT'İNİZİ KULLANIN
            await apiClient.patch(`/api/v1/admin/orders/${order.id}/status`, requestBody);

            toast.success("Sipariş başarıyla işlendi!");
            // Veriyi yeniden çekerek listeyi güncelle
            fetchOrdersForCurrentTab();

        } catch (err) {
            console.error("Sipariş işlenirken API hatası:", err);
            toast.error(`Sipariş işlenirken hata: ${err.response?.data?.message || err.message}`);
            // Hata durumunda bir şey yapmaya gerek yok, liste zaten yeniden çekilecek
            // veya optimistic UI kullanılıyorsa geri alınabilir.
        } finally {
            setActionLoading(null);
        }
    };

    const openDetailModal = (order) => { setSelectedOrderForModal(order); setIsDetailModalOpen(true); };
    const closeDetailModal = () => { setIsDetailModalOpen(false); setSelectedOrderForModal(null); };


    const renderOrderTable = (ordersToRender) => {
        // Yükleme durumu artık fetchOrdersForCurrentTab içinde yönetiliyor,
        // bu yüzden buradaki isLoading kontrolü daha çok "veri yok" durumları için.
        if (isLoading && ordersToRender.length === 0) {
            return <div className={styles.tableLoadingState}><LoadingSpinner /><p>Siparişler yükleniyor...</p></div>;
        }
        if (!isLoading && error && ordersToRender.length === 0) {
            return (
                <div className={styles.errorPanel}>
                    <span className={styles.errorPanelIcon} aria-hidden="true">⚠</span>
                    <p className={styles.errorPanelMessage}>
                        Sipariş verileri alınamadı. Bağlantıyı kontrol edip tekrar deneyin.
                    </p>
                    <button
                        type="button"
                        className={styles.retryButton}
                        onClick={fetchOrdersForCurrentTab}
                    >
                        Tekrar dene
                    </button>
                </div>
            );
        }
        if (!isLoading && ordersToRender.length === 0) {
            const currentTabConfig = tabConfig.find(tab => tab.key === activeTabKey);
            return <p className={styles.emptyState}>Gösterilecek "{currentTabConfig?.label || activeTabKey}" siparişi bulunmuyor.</p>;
        }

        return (
            <div className={styles.tableScroll}>
            <table className={styles.ordersTable}>
                <thead>
                    <tr>
                        <th>Sipariş ID</th>
                        <th>Kullanıcı ID</th>
                        <th>Ürün ID</th>
                        <th>Platform</th>
                        <th>Durum</th>
                        <th>Oluşturulma T.</th>
                        <th>İşlemler</th>
                    </tr>
                </thead>
                <tbody>
                    {ordersToRender.map(order => (
                        <tr key={order.id}>
                            <td
                                className={`${styles.orderIdCell} num`}
                                title="ID'yi kopyalamak için tıkla"
                                onClick={(e) => handleCopyOrderId(order.id, e)}
                            >
                                {order.id}
                                {copiedId === order.id && <span className={styles.copyFeedback}>Kopyalandı!</span>}
                            </td>                            <td className="num">{order.userId}</td>
                            <td className="num">{order.clientProductId}</td>
                            <td>
                                <span className={`${styles.platformBadge} ${order.platform === 'manual' ? styles.platformManual : ''}`}>
                                    {order.platform === 'manual' ? 'Manuel' : order.platform}
                                </span>
                            </td>
                            <td className={`${styles.statusCell} ${styles[(order.status || 'unknown').toLowerCase().replace(/_/g, '')]}`}>
                                {statusDisplayMap[order.status] || order.status}
                            </td>
                            <td className="num">{new Date(order.createdAt).toLocaleString('tr-TR')}</td>
                            <td>
                                <button
                                    onClick={() => openDetailModal(order)}
                                    className={`${styles.actionButton} ${styles.detailButton}`}
                                    title="Sipariş Detaylarını Gör"
                                >
                                    Detay
                                </button>
                                {/* 'İşleme Al' butonu kaldırıldı */}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            </div>
        );
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1 className={styles.title}>Sipariş Yönetimi</h1>
                <button
                    type="button"
                    className={styles.manualEsimButton}
                    onClick={() => setIsManualEsimModalOpen(true)}
                >
                    ＋ Manuel eSIM
                </button>
            </div>

            <div className={styles.tabContainer}>
                {tabConfig.map(tab => (
                    <button
                        key={tab.key}
                        className={`${styles.tabButton} ${activeTabKey === tab.key ? styles.activeTab : ''}`}
                        onClick={() => setActiveTabKey(tab.key)}
                        disabled={isLoading}
                    >
                        {tab.label}
                        {/* Sayaç yalnızca aktif sekme için API'den gelen totalItems'ı gösterir;
                            diğer sekmeler için tıklanana kadar '·' ile bekletilir. */}
                        <span className={`${styles.tabCount} num`}>
                            {activeTabKey === tab.key ? totalItems : '·'}
                        </span>
                    </button>
                ))}
            </div>

            <div className={styles.tabContent}>
                {renderOrderTable(displayedOrders)}
            </div>

            {/* Sayfalama Kontrolleri */}
            {totalPages > 1 && !isLoading && displayedOrders.length > 0 && (
                <div className={styles.paginationControls}>
                    <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1 || isLoading}>
                        « Önceki
                    </button>
                    <span> Sayfa {currentPage} / {totalPages} </span>
                    <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages || isLoading}>
                        Sonraki »
                    </button>
                    {/* Sayfa başına öğe seçimi (isteğe bağlı) */}
                    <select value={itemsPerPage} onChange={(e) => setItemsPerPage(Number(e.target.value))} disabled={isLoading} className={styles.itemsPerPageSelect}>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </select>
                    <span className={styles.totalItemsInfo}>Toplam {totalItems} sipariş</span>
                </div>
            )}


            {selectedOrderForModal && (
                <Modal
                    isOpen={isDetailModalOpen}
                    onClose={closeDetailModal}
                    title={`Sipariş Detayı: #${selectedOrderForModal.id.substring(0, 8)}...`}
                >
                    <div className={styles.modalOrderDetails}>
                        {/* ... (Modal içeriği API'den gelen yeni alanlara göre güncellenmeli) ... */}
                        <p><strong>Sipariş ID:</strong> <span className="num">{selectedOrderForModal.id}</span></p>
                        <p><strong>Kullanıcı ID:</strong> <span className="num">{selectedOrderForModal.userId}</span></p>
                        <p><strong>Ürün ID:</strong> <span className="num">{selectedOrderForModal.clientProductId}</span></p>
                        <p><strong>Mağaza İşlem ID:</strong> <span className="num">{selectedOrderForModal.storeTransactionId || '-'}</span></p>
                        <p><strong>Platform:</strong> {selectedOrderForModal.platform === 'manual' ? 'Manuel' : selectedOrderForModal.platform}</p>
                        <p><strong>Durum:</strong> <span className={`${styles.statusBadge} ${styles[(selectedOrderForModal.status || 'unknown').toLowerCase().replace(/_/g, '')]}`}>{statusDisplayMap[selectedOrderForModal.status] || selectedOrderForModal.status}</span></p>
                        {selectedOrderForModal.errorMessage && (
                            <p className={styles.modalErrorMessage}><strong>Hata Mesajı:</strong> {selectedOrderForModal.errorMessage}</p>
                        )}
                        <p><strong>Oluşturulma Tarihi:</strong> <span className="num">{new Date(selectedOrderForModal.createdAt).toLocaleString('tr-TR')}</span></p>
                        <p><strong>Güncellenme Tarihi:</strong> <span className="num">{new Date(selectedOrderForModal.updatedAt).toLocaleString('tr-TR')}</span></p>
                        <hr className={styles.modalDivider} />
                        <div className={styles.modalActions}>
                            <button onClick={closeDetailModal} className={`${styles.modalButton} ${styles.cancelButton}`}>
                                Kapat
                            </button>
                            {/* 'Siparişi İşleme Al' butonu kaldırıldı */}
                        </div>
                    </div>
                </Modal>
            )}

            {isManualEsimModalOpen && (
                <ManualEsimModal
                    isOpen={isManualEsimModalOpen}
                    onClose={() => setIsManualEsimModalOpen(false)}
                    onCreated={fetchOrdersForCurrentTab}
                />
            )}
        </div>
    );
};

export default OrdersPage;